import { useState, useEffect, useMemo } from 'react';
import {
  XMarkIcon,
  VideoCameraIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  MagnifyingGlassIcon,
  FolderOpenIcon,
  FolderIcon,
} from '@heroicons/react/24/outline';

/**
 * Which camera goes in this slot — the Live Wall's camera picker for ONE node.
 *
 * Shared by both products, because both ask the identical question of the identical data: the
 * local dashboard about the machine it runs on, and the cloud's per-site Live Wall about the
 * site in its title bar. The cloud's all-sites picker
 * (components/multi-site/CameraPickerModal) is the one that differs — it drills
 * sites → groups → cameras, and there is no site level to drill here.
 *
 * Purely presentational: the host passes the already-fetched cameras and flat groups, which is
 * what lets one component serve a local REST call and a tunnelled proxy call without knowing
 * which it got. Camera shape is the one both `/cameras` responses use — `{ id, name,
 * groups: [{ id }] }` — since the cloud's is the local one, proxied.
 */
interface Group {
  id: string;
  name: string;
  parent_id?: string | null;
}

interface Camera {
  id: string;
  name: string;
  /** As `/cameras` returns them — the groups this camera belongs to. */
  groups?: { id: string }[];
}

export interface LiveWallCameraPickerProps {
  open: boolean;
  onClose: () => void;
  onSelect: (cameraId: string, cameraName: string) => void;
  cameras: Camera[];
  /** Flat list — the tree is rebuilt here from parent_id. */
  groups: Group[];
  loading: boolean;
  /** Camera ids already on the wall; offered but not selectable. */
  usedCameraIds: Set<string>;
}

type DrillLevel = 'groups' | 'cameras';

export default function CameraPickerModal({
  open,
  onClose,
  onSelect,
  cameras,
  groups,
  loading,
  usedCameraIds,
}: LiveWallCameraPickerProps) {
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [drillLevel, setDrillLevel] = useState<DrillLevel>('groups');
  const [search, setSearch] = useState('');

  // Reset on every open — a picker that reopens two levels deep in a group the operator
  // was last in is a puzzle, not a shortcut.
  useEffect(() => {
    if (open) {
      setSelectedGroupId(null);
      setDrillLevel('groups');
      setSearch('');
    }
  }, [open]);

  const { topLevelGroups, ungroupedCameras, groupCamerasMap } = useMemo(() => {
    const camMap = new Map<string, Camera[]>();
    const grouped = new Set<string>();

    for (const cam of cameras) {
      for (const g of cam.groups || []) {
        const arr = camMap.get(g.id) || [];
        arr.push(cam);
        camMap.set(g.id, arr);
        grouped.add(cam.id);
      }
    }

    // A group whose parent isn't in the list is shown at the top level, not hidden. That is
    // the normal shape for a restricted user: /groups/for-filter returns only the groups
    // holding cameras they can see, so a sub-group can arrive without its parent — and
    // filtering on `!parent_id` alone would make its cameras unreachable in the picker.
    const known = new Set(groups.map((g) => g.id));

    return {
      topLevelGroups: groups.filter((g) => !g.parent_id || !known.has(g.parent_id)),
      ungroupedCameras: cameras.filter((c) => !grouped.has(c.id)),
      groupCamerasMap: camMap,
    };
  }, [cameras, groups]);

  const selectedGroupCameras = selectedGroupId ? groupCamerasMap.get(selectedGroupId) || [] : [];
  const childGroups = useMemo(
    () => (selectedGroupId ? groups.filter((g) => g.parent_id === selectedGroupId) : []),
    [groups, selectedGroupId],
  );

  const matches = (name: string) => !search || (name || '').toLowerCase().includes(search.toLowerCase());

  if (!open) return null;

  function goToGroup(groupId: string) {
    setSelectedGroupId(groupId);
    setDrillLevel('cameras');
    setSearch('');
  }

  function goBack() {
    // Climb one level of the group tree rather than jumping to the root — a sub-group's
    // "back" means its parent, unless that parent isn't in the list (see topLevelGroups),
    // in which case this group WAS the top level and back means out.
    const current = groups.find((g) => g.id === selectedGroupId);
    if (current?.parent_id && groups.some((g) => g.id === current.parent_id)) {
      setSelectedGroupId(current.parent_id);
    } else {
      setSelectedGroupId(null);
      setDrillLevel('groups');
    }
    setSearch('');
  }

  function pick(cam: Camera) {
    if (usedCameraIds.has(cam.id)) return;
    onSelect(cam.id, cam.name);
    onClose();
  }

  const title =
    drillLevel === 'groups'
      ? 'Select Camera'
      : groups.find((g) => g.id === selectedGroupId)?.name || 'Cameras';

  /** One row per camera — the same row whether it came from a group or the ungrouped list. */
  const cameraRow = (cam: Camera) => {
    const isUsed = usedCameraIds.has(cam.id);
    return (
      <button
        key={cam.id}
        onClick={() => pick(cam)}
        disabled={isUsed}
        className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
          isUsed ? 'opacity-40 cursor-not-allowed' : 'hover:bg-accent/50 cursor-pointer'
        }`}
      >
        <VideoCameraIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        <span className="flex-1 text-left text-foreground truncate">{cam.name}</span>
        {isUsed && <span className="text-[10px] text-muted-foreground">In grid</span>}
      </button>
    );
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />

      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-md max-h-[70vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <div className="flex items-center gap-2 min-w-0">
              {drillLevel !== 'groups' && (
                <button
                  onClick={goBack}
                  className="p-1 -ml-1 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label="Back"
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                </button>
              )}
              <h3 className="text-sm font-semibold text-foreground truncate">{title}</h3>
            </div>
            <button
              onClick={onClose}
              className="p-1 text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
              aria-label="Close"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>

          {/* Search */}
          <div className="px-4 py-2 border-b border-border/50">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={drillLevel === 'groups' ? 'Search groups or cameras…' : 'Search cameras…'}
                className="w-full pl-8 pr-3 py-1.5 rounded-lg text-sm bg-background border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-brand"
                autoFocus
              />
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-brand" />
                <span className="ml-2 text-sm text-muted-foreground">Loading…</span>
              </div>
            ) : drillLevel === 'groups' ? (
              <div className="py-1">
                {topLevelGroups.filter((g) => matches(g.name)).map((group) => {
                  const camCount = (groupCamerasMap.get(group.id) || []).length;
                  const subCount = groups.filter((g) => g.parent_id === group.id).length;
                  return (
                    <button
                      key={group.id}
                      onClick={() => goToGroup(group.id)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-accent/50 transition-colors"
                    >
                      <FolderOpenIcon className="h-4 w-4 text-amber-500 flex-shrink-0" />
                      <span className="flex-1 text-left text-foreground font-medium truncate">{group.name}</span>
                      <span className="text-[11px] text-muted-foreground">
                        {camCount} cam{camCount !== 1 ? 's' : ''}
                        {subCount > 0 && ` · ${subCount} sub`}
                      </span>
                      <ChevronRightIcon className="h-4 w-4 text-muted-foreground" />
                    </button>
                  );
                })}

                {topLevelGroups.length > 0 && ungroupedCameras.length > 0 && (
                  <div className="px-4 py-1.5">
                    <div className="border-t border-border/50" />
                    <p className="text-[10px] text-muted-foreground mt-1.5 font-medium uppercase tracking-wider">
                      Ungrouped
                    </p>
                  </div>
                )}

                {ungroupedCameras.filter((c) => matches(c.name)).map(cameraRow)}

                {topLevelGroups.length === 0 && ungroupedCameras.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-8">No cameras on this node</p>
                )}
              </div>
            ) : (
              <div className="py-1">
                {childGroups.filter((g) => matches(g.name)).map((group) => {
                  const camCount = (groupCamerasMap.get(group.id) || []).length;
                  return (
                    <button
                      key={group.id}
                      onClick={() => goToGroup(group.id)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-accent/50 transition-colors"
                    >
                      <FolderIcon className="h-4 w-4 text-amber-500 flex-shrink-0" />
                      <span className="flex-1 text-left text-foreground font-medium truncate">{group.name}</span>
                      <span className="text-[11px] text-muted-foreground">
                        {camCount} cam{camCount !== 1 ? 's' : ''}
                      </span>
                      <ChevronRightIcon className="h-4 w-4 text-muted-foreground" />
                    </button>
                  );
                })}

                {childGroups.length > 0 && selectedGroupCameras.length > 0 && (
                  <div className="px-4 py-1.5">
                    <div className="border-t border-border/50" />
                    <p className="text-[10px] text-muted-foreground mt-1.5 font-medium uppercase tracking-wider">
                      Cameras
                    </p>
                  </div>
                )}

                {selectedGroupCameras.filter((c) => matches(c.name)).map(cameraRow)}

                {childGroups.length === 0 && selectedGroupCameras.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    {search ? 'No cameras match' : 'No cameras in this group'}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
